/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @angular-eslint/no-output-on-prefix */
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { compareAsc, formatISO } from 'date-fns';
import L from 'leaflet';
import { combineLatest, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { RetrieveTaskByIdReply, TaskSearch, UserCalendar, UserData, ZombieData, Task, TaskStatusEnum } from '../../modules/admin/tasks/tasks.types';
import { NextServicio } from '../../modules/admin/services/services.types';
import { ServicesService } from '../../modules/admin/services/services.service';
import { TasksService } from '../../modules/admin/tasks/tasks.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';

@Component({
    selector: 'task-detail-dialog',
    templateUrl: './task-detail-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskDetailDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    @Output() readonly onClose = new EventEmitter<void>();

    task: TaskSearch;
    taskById: Task;
    taskByIdReply: RetrieveTaskByIdReply;
    service: NextServicio;

    updatingTask: boolean = false;
    hasChanges = false;

    openEditSidebar = false;

    calendarBelongsTo = '';
    selectedDayUserTasks: UserCalendar[] = [];
    nextDayUserTasks: UserCalendar[] = [];

    selectedUser: UserData;
    selectedZombie: ZombieData;

    virtualZombies: ZombieData[] = [];
    maxZombieNumber: number = 0;

    changeUserZombieLink = false;

    editButton: boolean = false;
    // Map variables
    // private marker: L.Marker;
    // private icon = L.icon({
    //     iconUrl: '/leaflet/images/marker-icon.png',
    //     shadowUrl: '/leaflet/images/marker-shadow.png',
    //     iconRetinaUrl: '/leaflet/images/marker-icon-2x.png',

    //     iconSize: [25, 41], // size of the icon
    //     shadowSize: [32, 32], // size of the shadow
    //     iconAnchor: [10, 40], // point of the icon which will correspond to marker's location
    //     shadowAnchor: [2, 30],  // the same for the shadow
    // });
    private map: L.Map;

    private unsubcribeAll: Subject<void> = new Subject();


    constructor(
        private servicesService: ServicesService,
        private tasksService: TasksService,
        private _fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService
    ) { }

    ngOnInit() {
        combineLatest([
            this.tasksService.task$,
            this.tasksService.taskById$,
            this.servicesService.service$,
            this.tasksService.editButton$,
            this.tasksService.openEditSidebar$
        ]).pipe(takeUntil(this.unsubcribeAll))
            .subscribe(([task, taskById, service, editButton, openEditSidebar]) => {
                this.task = task;
                this.service = service;
                this.editButton = editButton;
                this.openEditSidebar = openEditSidebar;
                this.taskById = taskById?.task;
                if (this.editButton && this.taskById && !task?.copy && !task?.copyCantEdit &&
                    (task?.status?.status === 'CANCELLED' || task?.status?.status === 'WORKING' || task?.status?.status === 'DONE' || task?.status?.status === 'DONE_WITH_ERROR'
                    || task?.status?.status === 'REJECTED')) {
                    this.copyInNewTask(this.taskById, 'copyCantEdit');
                    this.openEditSidebar = true;
                } else if (this.editButton) {
                    this.toggleUpdating();
                }
                if (task?.task?.taskId === 'new' && !task?.copy && !task.copyCantEdit) {
                    this.openEditSidebar = true;
                }
            });
    }

    ngOnDestroy() {
        this.unsubcribeAll.next();
        this.unsubcribeAll.complete();
    }

    ngAfterViewInit() {
        this.initMap();
    }

    close() {
        this.onClose.next(null);
    }

    isDelayed() {
        return this.taskById?.timeRemainingToStart - 60 > 0;
    }

    async toggleUpdating(str: string = '') {
        if (str === 'copyCantEdit') {
            this.copyInNewTask(this.taskById, 'copyCantEdit');
            const taskByIdReply = await firstValueFrom(this.tasksService.taskById$);
            this.taskById = taskByIdReply?.task;
            this.openEditSidebar = !this.openEditSidebar;
        } else if (str === 'copy') {
            const confirm = await this.confirm('confirmCopyTask');
            if (confirm) {
                this.copyInNewTask(this.taskById, 'copy');
                const taskByIdReply = await firstValueFrom(this.tasksService.taskById$);
                this.taskById = taskByIdReply?.task;
                this.openEditSidebar = !this.openEditSidebar;
            }
        } else {
            this.openEditSidebar = !this.openEditSidebar;
        }
    }

    async copyInNewTask(t: Task, str: string = '') {
        const task: Task = t;
        task.taskId = 'new';
        t.scheduled_from_date = this.isValidDate(t.scheduled_from_date ?? t.startTaskDate) ? t.scheduled_from_date : null;
        t.scheduled_to_date = this.isValidDate(t.scheduled_to_date ?? t.endTaskDate) ? t.scheduled_to_date : null;
        task.serviceId = t.serviceId;
        task.belongsTo = t.belongsTo;
        task.taskTypeId = t.taskTypeId;
        task.taskTypeUri = t.taskTypeUri;
        task.taskTypeName = t.taskTypeName;
        task.status = TaskStatusEnum.WITHERROR;
        task.timestamp = formatISO(new Date());
        task.copy = (str === 'copy') ? true : false;
        task.oldTaskId = this.task?.task?.taskId;
        task.copyCantEdit = (str === 'copyCantEdit') ? true : false;

        const taskByIdReply: RetrieveTaskByIdReply = {status: 200, task: task};
        if (str === '') {
            this.tasksService.setTaskById(taskByIdReply);
        }
    }

    isValidDate(str: string) {
        if (!str) {
            return true;
        }
        const date = new Date(str);
        date.setHours(2);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        const today = new Date();
        today.setHours(2);
        today.setMinutes(0);
        today.setSeconds(0);
        today.setMilliseconds(0);
        return date > today || compareAsc(today, date) === 0;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private confirm(_message: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: this.transloco.translate(_message),
            actions: {
                confirm: {
                    label: this.transloco.translate('accept')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }

    private initMap(): void {
        try {
            //Create new map centered on Madrid
            if (!this.map) {
                this.map = L.map('map', {
                    center: [40.416877, -3.703526],
                    zoom: 16,
                    doubleClickZoom: false,
                });
            }

            //TODO go to coordinates when available
            // if (this.task && this.task.latitude && this.task.longitude) {
            //     this.centerMap(this.task.latitude, this.task.longitude);
            //     this.paintMarker(this.task.latitude, this.task.longitude);
            // }

            //Set source of tiles
            const tiles = L.tileLayer(
                'https://maps.krama.es/hot/{z}/{x}/{y}.png',
                {
                    maxZoom: 18,
                    minZoom: 3,
                    attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                });

            // Add tile to map
            tiles.addTo(this.map);
        } catch (error) {
            console.warn(error);
        }
    }
}
